// import

import {format} from 'date-fns';
import {styled} from 'goober';
import * as R from 'ramda';
import type {ComponentProps} from 'react';

import type {PersonNode, PostTagNode, SeriesNode, SupportTagNode, TaxTerm, UrlNode} from 'libs/model';
import {Calendar, Tag, User, At} from 'modules/Icon';
import {Link} from 'modules/Link';

// types

type TagNode = PostTagNode | SupportTagNode;

type Props = ComponentProps<typeof Foot> & {
  node: UrlNode<{
    tagRefs?: TagNode[];
    audienceRef?: TaxTerm;
    authorRef?: PersonNode;
    seriesRef?: SeriesNode;
  }>;
};

// fns

const sortTags = R.sortBy<TagNode>((t) => (t.body.length ? 0 : 1));

// component

export function Meta(props: Props) {
  const {node: {meta}, ...rest} = props;

  return (
    <Foot {...rest}>
      <div className="line">
        <time className="pill date" dateTime={meta.publishDate}>
          <Calendar />&thinsp;
          <span>{format(new Date(meta.publishDate), 'MMM d, yyyy')}</span>
        </time>

        {meta.authorRef ? (
          <Link className="pill author" href={meta.authorRef.meta.link}>
            <At />&thinsp;
            <span>{meta.authorRef.meta.title}</span>
          </Link>
        ) : null}

        {meta.audienceRef ? (
          <span className="pill tier">
            <User />&thinsp;
            <span>{meta.audienceRef.title}</span>
          </span>
        ) : null}

        {sortTags(meta.tagRefs ?? []).map((tag) => (
          tag.body.length ? (
            <Link key={tag.meta.id} className="pill tag" href={tag.meta.link}>
              <Tag />&thinsp;
              <span>{tag.meta.title}</span>
            </Link>
          ) : (
            <span key={tag.meta.id} className="pill tag">
              <Tag />&thinsp;
              <span>{tag.meta.title}</span>
            </span>
          )
        )).filter(Boolean)}
      </div>

      {meta.seriesRef ? (
        <div key={meta.seriesRef.meta.id} className="line">
          <Link
            className="pill series"
            href={meta.seriesRef.meta.link}
          >
            <User />&thinsp;
            <span>{meta.seriesRef.meta.title} #{(meta.seriesRef.meta.posts?.indexOf(meta.id) ?? 0) + 1}</span>
          </Link>
        </div>
      ) : null}
    </Foot>
  );
}

// styles

const Foot = styled('footer')`
  font-size: 1rem;
  line-height: 2rem;

  .line {
    height: 2rem;
    overflow: hidden;
  }

  .pill {
    display: inline-block;
    margin-right: 1rem;

    svg { color: var(--pill); }
    &:any-link span { text-decoration: underline var(--pill); }
    &:any-link:hover span { color: var(--pill); }
  }

  svg {
    position: relative;
    top: 0.125rem;
    width: 1rem;
  }

  .date { --pill: var(--red-50); }
  .author { --pill: var(--orange-60); }
  .tier { --pill: var(--yellow-70); }
  .tag { --pill: var(--blue); }
  .series { --pill: var(--green); }
`;
